<template>
  <div v-if="showFilters" class="filter-container" :class="{ 'gray-background': isMediaType }">
    <div class="filter-options">
      <InputField v-for="(item, index) in filtersConfig" :key="item.id" :fieldConfig="item">
        <component
          :is="item.type"
          :fieldConfig="item"
          :filtersConfig="filtersConfig"
          :values="formFilters"
          :index="index"
          @change="handleChange"
        />
      </InputField>
    </div>

    <div class="filter-footer" v-if="isMediaType">
      <BbBtn size="small" type="secondary" class="more-container">
        <BbIcon size="small" name="filters" />
        <TextBody i18n-key="author.filters.moreFilters" tag="span" :body-type="1" weight="medium" />
      </BbBtn>
      <div class="filter-actions">
        <BbBtn size="x-small" type="tertiary" @click="setInitialValues" color="#6222e3">
          <TextBody i18n-key="author.filters.clearFilter"></TextBody>
        </BbBtn>
      </div>
    </div>
  </div>
  <div v-else>
    <div v-if="isConversationType">
      <ButtonWithIcon
        icon="filters"
        :text-key="!filtersCount ? 'assist.filters.refineYourSearch' : ''"
        :text="filtersCount ? `Filtering on ${filterValues}` : ''"
        iconSize="small"
        @click="toggleContainer"
      />
    </div>
  </div>

  <div class="collapsed-filters" v-if="isMediaType">
    <div class="filters-count">
      <BbBtn class="filter-toggle" type="tertiary" @click="toggleContainer">
        <template #prepend>
          <BbIcon :name="showFilters ? 'chevron-up' : 'filters'" size="small" />
        </template>
        Filter
      </BbBtn>
      <div class="documents-amount">{{ documentsAmount }}</div>
    </div>
  </div>
</template>

<script>
import { computed, inject, onMounted, ref, watch } from 'vue';
import useFormFilters from '~/composables/useFormFilters';
import useModals from '~/composables/useModals';

import { FILTER_TYPE } from '~/constants/documents';

import InputField from '~/services/filter-form-service/components/InputField';
import components from '~/services/filter-form-service/components';
import TextBody from '~/components/common/TextBody';
import useDocuments from '~/composables/useDocuments';
import ButtonWithIcon from '~/components/conversation/ButtonWithIcon.vue';

export default {
  components: { ButtonWithIcon, InputField, ...components, TextBody },
  props: {
    type: {
      type: String,
      default: FILTER_TYPE.document,
    },
  },
  setup(props) {
    const filterService = inject('filterFormService');

    const { showFilters, setShowFilters } = useModals();
    const { formFilters, setFilters, setInitialFilters } = useFormFilters();
    const { totalMediaImages } = useDocuments();

    const isMediaType = props.type === FILTER_TYPE.media;
    const isConversationType = props.type === FILTER_TYPE.document;

    const filtersConfig = ref([]);

    const documentsAmount = computed(() => {
      const amount = totalMediaImages.value;
      const label = amount !== 1 ? 'results' : 'result';

      return [amount, label].join(' ').trim();
    });

    const filtersCount = computed(() => Object.values(formFilters.value).filter((value) => value.trim() !== '').length);

    const filterValues = computed(() => {
      const fieldMap = new Map(filtersConfig.value.map((f) => [f.id, f.optionList]));

      // Iterate over the object's entries and build the resulting string
      return Object.entries(formFilters.value).reduce((acc, [key, value]) => {
        const list = fieldMap.get(key);

        // If the field is found, find the pair
        if (list) {
          const pair = list.find((a) => a.value === value);

          // If pair is found, concatenate its label
          if (pair) {
            return acc ? acc + ', ' + pair.label : pair.label;
          }
        }

        return acc;
      }, '');
    });

    const handleChange = (event) => {
      const { id, value } = event;
      if (id) {
        updateValue(id, value);
      }
    };

    const updateValue = (id, value) => {
      setFilters({ key: id, value });
    };

    const setInitialValues = () => {
      setInitialFilters();
    };

    const toggleContainer = () => {
      setShowFilters(!showFilters.value);
    };

    onMounted(async () => {
      filtersConfig.value = await filterService.getFilterForm(props.type);

      if (!formFilters.value || !Object.keys(formFilters.value).length) {
        setInitialValues();
      }
    });

    watch(
      () => filtersConfig.value,
      (newVal) => {
        if (newVal.length) {
          setShowFilters(true);
        }
      },
    );

    watch(
      () => formFilters.value,
      async () => {
        filtersConfig.value = await filterService.getUpdatedFilterForm(props.type, formFilters.value);
      },
    );

    return {
      formFilters,
      filtersCount,
      filtersConfig,
      filterValues,
      handleChange,
      setInitialValues,
      showFilters,
      toggleContainer,
      isMediaType,
      isConversationType,
      documentsAmount,
    };
  },
};
</script>

<style scoped lang="scss">
.collapsed-filters {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  flex-direction: row;
  background-color: $bb-neutral-6;
  border-radius: $bb-spacing-5;

  .filters-count {
    display: flex;
    align-items: center;
    font-weight: 500;
    gap: $bb-spacing-3;
    height: $bb-spacing-10;
    padding: 0 $bb-spacing-4;

    .filter-toggle {
      margin-top: 5px;
    }
  }
}

.gray-background {
  background-color: $bb-neutral-6 !important;
}

.filter-container {
  margin: $bb-spacing-3 0 $bb-spacing-3 0;
  display: flex;
  flex-direction: column;
  padding: $bb-spacing-4 $bb-spacing-5 !important;
  border-radius: $bb-spacing-1 !important;

  .filter-header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .filter-actions {
      display: flex;
      align-items: center;
      gap: $bb-spacing-1;
    }
  }

  .filter-footer {
    display: flex;
    align-items: center;
    padding-top: $bb-spacing-2;
    justify-content: flex-start;
    gap: $bb-spacing-4;

    .more-container {
      :deep(.v-btn__content) {
        display: flex;
        align-items: center;
        gap: $bb-spacing-2;
      }
    }

    .filter-actions {
      display: flex;
      align-items: center;
      gap: $bb-spacing-1;
    }
  }

  .filter-options {
    display: flex;
    flex-direction: row;
    gap: $bb-spacing-1;
    flex-wrap: wrap;
  }
}
</style>
